import { getArchetype } from "lib/archetypes";

const TONES = {
  success: "bg-success-subtle text-success-fg",
  warning: "bg-warning-subtle text-warning-fg",
  danger: "bg-danger-subtle text-danger-fg",
  info: "bg-info-subtle text-info-fg",
  neutral: "bg-surface-subtle text-fg-muted",
  brand:
    "bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200",
};

const SIZES = {
  sm: "px-2 py-0.5 text-2xs",
  md: "px-2.5 py-0.5 text-xs",
};

/**
 * Badge — compact status/label pill.
 *
 * @param {object} props
 * @param {"success"|"warning"|"danger"|"info"|"neutral"|"brand"|"archetype"} [props.tone="neutral"]
 *   Use `statusTone(status)` from `lib/statusTone` to pick a tone for domain statuses.
 *   `archetype` colors the pill from `lib/archetypes` using the `archetype` key.
 * @param {string} [props.archetype] Archetype key for `tone="archetype"` (case-insensitive). Unknown keys
 *   fall back to neutral. Without children the badge shows the archetype's `shortLabel`.
 * @param {"sm"|"md"} [props.size="md"]
 * @param {boolean} [props.dot=false] Leading colored dot (inherits the text color).
 * @param {string} [props.swatch] CSS color for a leading data-driven dot (tag, band or series color).
 * @param {string} [props.className]
 */
function Badge({
  tone = "neutral",
  archetype,
  size = "md",
  dot = false,
  swatch,
  className,
  children,
  ...rest
}) {
  const archetypeInfo = tone === "archetype" ? getArchetype(archetype) : null;
  const toneClass = archetypeInfo ? archetypeInfo.badgeClass : TONES[tone] || TONES.neutral;

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium",
        toneClass,
        SIZES[size] || SIZES.md,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {dot && (
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-current"
        />
      )}
      {swatch && (
        <span
          aria-hidden="true"
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: swatch }}
        />
      )}
      {children ?? archetypeInfo?.shortLabel ?? archetype}
    </span>
  );
}

export default Badge;
